import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { usePagination } from '@/hooks/usePagination';
import { useDebounce } from '@/hooks/useDebounce';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { DataTable } from '@/components/common/DataTable';
import { UserFilterSidebar } from '@/components/features/users/UserFilterSidebar';
import { AddUserModal } from '@/components/features/users/AddUserModal';
import { User, UserRole } from '@/types/user.types';
import { UserX, Filter, Eye, Plus, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { useI18n } from '@/contexts/I18nContext';

interface FilterValues {
  search: string;
  role: string;
}

const initialFilters: FilterValues = {
  search: '',
  role: '',
};

export default function UsersList() {
  const navigate = useNavigate();
  const { tp } = useI18n();
  const { data, get, loading } = useApi();
  const pagination = usePagination();

  const [filters, setFilters] = useState<FilterValues>(initialFilters);
  const [showMobileFilters, setShowMobileFilters] = useState(false);
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);

  const debouncedSearch = useDebounce(filters.search, 300);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, debouncedSearch, filters.role]);

  const fetchUsers = async () => {
    try {
      const params: Record<string, any> = {
        page: pagination.page,
        limit: pagination.limit,
      };

      if (debouncedSearch) {
        params.search = debouncedSearch;
      }

      if (filters.role) {
        params.role = filters.role;
      }

      const response: any = await get('/api/v1/dashboard/users', params);

      if (response?.meta) {
        pagination.updatePagination({
          total_pages: response.meta.totalPages,
          total: response.meta.total,
        });
      }
    } catch (error) {
      console.error('Failed to fetch users:', error);
    }
  };

  const apiUsers = data?.list || [];
  const users: User[] = apiUsers.map((u: any) => ({
    id: u.id,
    display_name: u.name,
    email: u.email,
    phone: u.phone,
    role: u.role as UserRole,
    status: u.is_active ? 'active' : 'inactive',
    created_at: u.created_at,
    last_login: u.last_login,
  }));

  const handleClearFilters = () => {
    setFilters(initialFilters);
    pagination.reset();
  };

  const handleFilterChange = (key: keyof FilterValues, value: string) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value === 'all' ? '' : value,
    }));
  };

  const handleUserClick = (user: User) => {
    navigate(`/dashboard/users/${user.id}`);
  };

  const getRoleBadge = (role: string) => {
    const roleConfig: Record<string, { label: string; className: string }> = {
      admin: { label: tp.users.roleAdmin, className: 'bg-purple-500/10 text-purple-500 border-purple-500/20' },
      carrier: { label: tp.users.roleCarrier, className: 'bg-blue-500/10 text-blue-500 border-blue-500/20' },
      driver: { label: tp.users.roleDriver, className: 'bg-green-500/10 text-green-500 border-green-500/20' },
      tenant: { label: tp.users.roleTenant, className: 'bg-orange-500/10 text-orange-500 border-orange-500/20' },
      customer: { label: tp.users.roleCustomer, className: 'bg-gray-500/10 text-gray-500 border-gray-500/20' },
    };
    const config = roleConfig[role] || { label: role, className: '' };
    return <Badge variant="outline" className={config.className}>{config.label}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    if (status === 'active') {
      return <Badge className="bg-green-500/10 text-green-500 border-green-500/20">{tp.users.statusActive}</Badge>;
    }
    return <Badge variant="outline" className="text-muted-foreground">{tp.users.statusInactive}</Badge>;
  };

  const columns = [
    {
      header: tp.users.tableHeaderName,
      accessor: 'display_name' as keyof User,
      className: 'font-medium',
    },
    {
      header: tp.users.tableHeaderEmail,
      accessor: (row: User) => row.email || '-',
    },
    {
      header: tp.users.tableHeaderPhone,
      accessor: (row: User) => row.phone || '-',
    },
    {
      header: tp.users.tableHeaderRole,
      accessor: (row: User) => getRoleBadge(row.role),
    },
    {
      header: tp.users.tableHeaderStatus,
      accessor: (row: User) => getStatusBadge(row.status),
    },
    {
      header: tp.users.tableHeaderCreatedDate,
      accessor: (row: User) => new Date(row.created_at).toLocaleDateString('ar-EG'),
      className: 'text-muted-foreground',
    },
  ];

  const renderActions = (row: User) => (
    <Button
      variant="ghost"
      size="icon"
      className="h-8 w-8"
      onClick={(e) => { e.stopPropagation(); handleUserClick(row); }}
      title={tp.users.viewDetailsTooltip}
    >
      <Eye className="h-4 w-4" />
    </Button>
  );

  const hasActiveFilters = filters.search !== '' || filters.role !== '';

  const FilterSidebarContent = (
    <UserFilterSidebar
      filters={filters}
      onFilterChange={handleFilterChange}
      onClearFilters={handleClearFilters}
    />
  );

  return (
    <PageContainer>
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronRight className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold">{tp.users.pageTitle}</h1>
              <p className="text-sm text-muted-foreground">{tp.users.pageDescription}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowAddModal(true)}>
              <Plus className="h-4 w-4 ms-2" />
              {tp.users.addButton}
            </Button>
            <Sheet open={showMobileFilters} onOpenChange={setShowMobileFilters}>
              <SheetTrigger asChild>
                <Button variant="outline" size="icon" className="lg:hidden">
                  <Filter className="h-4 w-4" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72 p-4">
                <div className="mt-6">
                  {FilterSidebarContent}
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>

      <div className="flex gap-4 h-[calc(100vh-180px)]">
        <div className="hidden lg:block h-full overflow-y-auto">
          {FilterSidebarContent}
        </div>
        <div className="flex-1 min-w-0">
          {loading ? (
            <LoadingSkeleton type="table" rows={10} />
          ) : users.length === 0 ? (
            <EmptyState
              icon={<UserX className="h-16 w-16" />}
              title={tp.users.emptyStateTitle}
              description={tp.users.emptyStateDescription}
              action={
                hasActiveFilters
                  ? { label: tp.users.clearFiltersButton, onClick: handleClearFilters }
                  : undefined
              }
            />
          ) : (
            <DataTable
              data={users}
              columns={columns}
              selectable={true}
              selectedRows={selectedRows}
              onSelectionChange={setSelectedRows}
              actions={renderActions}
              pagination={{
                currentPage: pagination.page,
                totalPages: pagination.totalPages,
                total: pagination.total,
                onPageChange: pagination.goToPage,
              }}
              emptyMessage={tp.users.emptyStateTitle}
            />
          )}
        </div>
      </div>
      <AddUserModal
        open={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSuccess={fetchUsers}
      />
    </PageContainer>
  );
}
